<template>
  <tr :class="{ 'blue-grey darken-2': processed }">
    <td>
      <a :href="'http://' + item.domain" target="_blank">{{ item.domain }}</a>
    </td>
    <td colspan="3">
      <v-form ref="form">
        <v-row align="center" class="my-0">
          <v-col cols="6" lg="7">
            <v-row no-gutters align="center">
              <v-col>
                <tree-search
                  v-if="!processed || valid"
                  v-model="model.tree_id"
                  :rules="treeSearchRules"
                  :disabled="!valid || processed || loading"
                  mode="classification"
                  placeholder="Search With Global"
                  :country-code="'*'"
                  inline
                />
              </v-col>
              <v-col v-if="canViewTreePrediction()" cols="auto">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="ai-tree-button ml-2"
                      :loading="aiLoading"
                      :disabled="!valid || processed || loading"
                      v-bind="attrs"
                      v-on="on"
                      @click="generateTreeWithMav"
                    >
                      <v-icon small color="white">mdi-robot</v-icon>
                    </v-btn>
                  </template>
                  <span>Predict Tree ID</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
          <nv-show :header="model.effective_start_date ? 'Start Date' : ''" cols="2" no-placeholder>
            <plus-icon-button :disabled="isAddDisabled" @add="addLocalization" />
            <owl-date-picker
              v-model="model.effective_start_date"
              :disabled="processed || loading"
              label="Start Date"
              icon
              clearable
            />
            <span v-if="model.effective_start_date" class="mr-1">
              {{ model.effective_start_date }}
            </span>
          </nv-show>
          <v-col cols="4" lg="3" class="d-flex justify-end align-center">
            <v-btn-toggle v-model="valid" mandatory>
              <v-btn
                v-for="validState in validStates"
                :key="validState.text"
                :value="validState.value"
                :disabled="processed || loading"
                text
                small
              >
                {{ validState.text | humanize }}
              </v-btn>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <template v-if="item.locked_by === $auth.user.email && onTime(item.lock_expires_at)">
              <save-btn v-if="!processed" :loading="loading" class="ml-3" @click="save()" />
              <done-chip v-else class="ml-2" />
            </template>
            <expire-chip v-else />
          </v-col>
        </v-row>
        <v-row
          v-for="(row, index) in localizations.slice(0, Math.max(1, countries.length - 1))"
          :key="index"
          align="center"
          class="my-0"
        >
          <v-col cols="4" lg="5">
            <tree-search
              v-model="row.tree_id"
              :rules="treeSearchRules"
              :disabled="!valid || processed || loading"
              mode="classification"
              inline
              :country-code="getCountry(row.country)"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="row.country"
              :items="filteredCountries"
              label="Country"
              :disabled="processed || loading"
            ></v-select>
          </v-col>
          <v-col cols="1">
            <owl-date-picker
              v-model="model.effective_start_date"
              :disabled="processed || loading"
              label="Start Date"
              icon
              clearable
            />
          </v-col>
          <v-col cols="3" lg="3" class="d-flex justify-end align-center">
            <v-btn-toggle v-model="valid" mandatory>
              <v-btn
                v-for="validState in validStates"
                :key="validState.text"
                :value="validState.value"
                :disabled="processed || loading"
                text
                small
              >
                {{ validState.text | humanize }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="1">
            <v-btn icon :disabled="processed || loading" @click="removeLocalization(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </td>

    <!-- Channel Suggestion Dialog -->
    <v-dialog v-model="showChannelSuggestionDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6">
          Channel Entity Not Found
        </v-card-title>
        <v-card-text>
          Channel Entity is not present for the predicted Hierarchy [{{
            channelSuggestionData ? channelSuggestionData.parent_name : ''
          }}
          → {{ channelSuggestionData ? channelSuggestionData.brand_name : '' }}]. Would you like to
          create one?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dismissChannelSuggestion">No</v-btn>
          <v-btn color="primary" text @click="confirmCreateChannel">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Channel Creation Form -->
    <brand-channel-form
      v-model="channelCreateForm.show"
      :prefill="channelCreateForm.prefill"
      channel
      cls
    />

    <!-- Hierarchy Creation: Parent Form -->
    <parent-form
      v-model="hierarchyCreate.parentForm.show"
      :prefill="hierarchyCreate.parentForm.prefill"
      @saved="onParentCreated"
    />

    <!-- Hierarchy Creation: Brand Form -->
    <brand-channel-form
      v-model="hierarchyCreate.brandForm.show"
      :prefill="hierarchyCreate.brandForm.prefill"
      :post-endpoint="hierarchyCreate.brandPostEndpoint"
      @saved="onBrandCreated"
    />

    <!-- Hierarchy Creation: Channel Form -->
    <brand-channel-form
      v-model="hierarchyCreate.channelForm.show"
      :prefill="hierarchyCreate.channelForm.prefill"
      channel
      cls
      @saved="onChannelCreated"
    />
  </tr>
</template>

<script>
import Rules from 'bsm-validation-rules';
import MAV from '@/api/mav';
import TreeSearch from '@/components/mv/TreeSearch';
import ClassifyRowMixin from '@/components/cls/ClassifyRowMixin';
import OwlDatePicker from '@/components/OwlDatePicker';
import SaveBtn from '@/components/cls/SaveBtn.vue';
import DoneChip from '@/components/cls/DoneChip.vue';
import ExpireChip from '@/components/cls/ExpireChip.vue';
import PlusIconButton from '@/components/PlusIconButton.vue';
import { getConfigMap } from '@/helpers/filters';
import { submitFeedback } from '@/api/feedback';
import BrandChannelForm from '@/components/mv/BrandChannelForm';
import ParentForm from '@/components/mv/parents/ParentForm';
import { MavStreamBus } from '@/helpers/mavStreamBus';

const rules = {
  pattern: [Rules.presence()],
  treeId: [Rules.presence()],
};

export default {
  components: {
    ExpireChip,
    DoneChip,
    SaveBtn,
    OwlDatePicker,
    TreeSearch,
    PlusIconButton,
    BrandChannelForm,
    ParentForm,
  },
  mixins: [ClassifyRowMixin],
  data() {
    return {
      model: {
        pattern: this.item.domain,
        tree_id: null,
        effective_start_date: null,
      },
      rules,
      valid: true,
      treeSearchRules: [],
      validStates: [
        { text: 'valid', value: true },
        { text: 'invalid', value: false },
      ],
      localizations: [],
      countries: [
        { text: 'default', value: '*' },
        { text: 'us', value: 'us' },
      ],
      payloadArray: [],
      formattedPayload: null,
      aiLoading: false,
      aiGeneratedTreeId: null,
      aiSuggestionFull: null,
      confidenceScore: 0.8,
      feedbackSent: false,
      showChannelSuggestionDialog: false,
      channelSuggestionData: null,
      channelCreateForm: { show: false, prefill: null },
      hierarchyCreate: {
        prediction: null,
        parentForm: { show: false, prefill: null },
        brandForm: { show: false, prefill: null },
        channelForm: { show: false, prefill: null },
        brandPostEndpoint: null,
        createdParentId: null,
        createdBrandId: null,
      },
    };
  },
  computed: {
    filteredCountries() {
      return this.countries.filter(country => country.value !== '*');
    },
    getCountry() {
      return rowCountry => rowCountry || 'us';
    },
  },

  watch: {
    valid(val) {
      if (!val) {
        this.$refs.form.resetValidation();
        this.treeSearchRules = [];
      }
    },
  },
  created() {
    MavStreamBus.$on('mav-batch-apply', this.handleBatchApply);
  },
  beforeDestroy() {
    MavStreamBus.$off('mav-batch-apply', this.handleBatchApply);
  },
  methods: {
    async sendFeedback(action, finalTreeId) {
      try {
        await submitFeedback({
          input_string: this.item.domain,
          ai_suggestion: {
            tree_id: this.aiSuggestionFull?.tree_id || this.aiGeneratedTreeId,
            rationale: this.aiSuggestionFull?.rationale || '',
            confidence: this.aiSuggestionFull?.confidence || this.confidenceScore,
            entity_type: 'domain',
          },
          user_final_decision: {
            tree_id: finalTreeId,
            entity_type: 'domain',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },

    async generateTreeWithMav() {
      MavStreamBus.$emit('mav-stream-predict', {
        domain: this.item.domain,
        countries: 'us',
        itemId: this.item.id,
        callback: prediction => {
          this.applyStreamPrediction(prediction);
        },
      });
    },

    handleBatchApply({ domain, prediction }) {
      // Only apply if this row matches the domain from the batch result
      if (domain !== this.item.domain) return;
      if (!prediction) return;
      this.applyStreamPrediction(prediction);
    },

    applyStreamPrediction(prediction) {
      // If the agent suggested creating a new hierarchy
      if (prediction._needsCreation) {
        this.startHierarchyCreation(prediction);
        return;
      }

      // INVALID prediction — toggle the valid/invalid state, don't set tree_id
      if (prediction._isInvalid) {
        this.valid = false;
        this.model.tree_id = null;
        this.$flash('info', `Domain "${this.item.domain}" marked as invalid`);
        return;
      }

      // Map the streamed prediction using the same hierarchy fallback
      let tree_id = null;
      let confidence = prediction.overall_confidence;
      let hierarchyLevel = '';
      let hierarchyName = '';

      if (prediction.channel_id && prediction.channel_id !== 0) {
        tree_id = prediction.channel_id;
        confidence = prediction.channel_confidence;
        hierarchyLevel = 'Channel';
        hierarchyName = prediction.channel_name;
      } else if (prediction.brand_id && prediction.brand_id !== 0) {
        tree_id = prediction.brand_id;
        confidence = prediction.brand_confidence;
        hierarchyLevel = 'Brand';
        hierarchyName = prediction.brand_name;
      } else if (prediction.parent_id && prediction.parent_id !== 0) {
        tree_id = prediction.parent_id;
        confidence = prediction.parent_confidence;
        hierarchyLevel = 'Parent';
        hierarchyName = prediction.parent_name;
      }

      const mapped = {
        tree_id,
        confidence,
        rationale: `Predicted ${hierarchyLevel}: ${hierarchyName} (Parent: ${prediction.parent_name ||
          'N/A'}, Brand: ${prediction.brand_name || 'N/A'}, Channel: ${prediction.channel_name ||
          'N/A'})`,
        channel_id: prediction.channel_id,
        channel_name: prediction.channel_name,
        brand_id: prediction.brand_id,
        brand_name: prediction.brand_name,
        parent_id: prediction.parent_id,
        parent_name: prediction.parent_name,
        hierarchy_level: hierarchyLevel,
        is_channel_suggestion: prediction.is_channel_suggestion || false,
        channel_suggestion: prediction.channel_suggestion || null,
        validation_result: prediction.validation_result || null,
        ad_supported: prediction.ad_supported || 'unknown',
      };

      this.aiGeneratedTreeId = mapped.tree_id;
      this.aiSuggestionFull = mapped;
      this.model.tree_id = mapped.tree_id;

      if (mapped.validation_result && mapped.validation_result.is_valid === false) {
        this.valid = false;
        this.$flash(
          'warning',
          `Domain "${this.item.domain}" is ${mapped.validation_result.validation_type ||
            'invalid'}`,
        );
      }

      // if (mapped.is_channel_suggestion) {
      //   this.channelSuggestionData = mapped;
      //   this.showChannelSuggestionDialog = true;
      // }
    },

    confirmCreateChannel() {
      this.showChannelSuggestionDialog = false;
      const suggestion = this.channelSuggestionData.channel_suggestion || {};
      this.channelCreateForm.prefill = {
        name: suggestion.suggested_name || this.channelSuggestionData.channel_name || '',
        brand_id: suggestion.brand_id || this.channelSuggestionData.brand_id || null,
        parent_id: this.channelSuggestionData.parent_id || null,
        ad_supported:
          suggestion.ad_supported || this.channelSuggestionData.ad_supported || 'unknown',
        category_id: suggestion.category_id || null,
        category_name: suggestion.category_name || '',
        sub_category_id: suggestion.subcategory_id || null,
        sub_category_name: suggestion.subcategory_name || '',
      };
      this.channelCreateForm.show = true;
    },

    dismissChannelSuggestion() {
      this.showChannelSuggestionDialog = false;
      this.channelSuggestionData = null;
    },

    startHierarchyCreation(prediction) {
      this.hierarchyCreate.prediction = prediction;
      // Step 1: Create parent
      this.hierarchyCreate.parentForm.prefill = {
        name: prediction.parent_name || '',
      };
      this.hierarchyCreate.parentForm.show = true;
    },

    onParentCreated(savedPayload) {
      // FormMixin emits { current: axiosResponse, previous }
      const response = savedPayload.current ? savedPayload.current.data : savedPayload;
      const entity = response.item || response;
      const parentId = entity.id;
      this.hierarchyCreate.createdParentId = parentId;
      this.hierarchyCreate.parentForm.show = false;

      const prediction = this.hierarchyCreate.prediction;
      // Step 2: Create brand under the new parent
      this.hierarchyCreate.brandPostEndpoint = `/parents/${parentId}/brand?country_code=*`;
      this.hierarchyCreate.brandForm.prefill = {
        name: prediction.brand_name || prediction.parent_name || '',
        parent_id: parentId,
        ad_supported: prediction.ad_supported || 'unknown',
        sub_category_id: prediction.subcategory_id || null,
        sub_category_name: prediction.subcategory_name || '',
        category_id: prediction.category_id || null,
        category_name: prediction.category_name || '',
      };
      this.hierarchyCreate.brandForm.show = true;
    },

    onBrandCreated(savedPayload) {
      const response = savedPayload.current ? savedPayload.current.data : savedPayload;
      const entity = response.item || response;
      const brandId = entity.id;
      this.hierarchyCreate.createdBrandId = brandId;
      this.hierarchyCreate.brandForm.show = false;

      const prediction = this.hierarchyCreate.prediction;
      // Step 3: Create channel under the new brand
      this.hierarchyCreate.channelForm.prefill = {
        name: prediction.channel_name || prediction.brand_name || '',
        brand_id: brandId,
        ad_supported: prediction.ad_supported || 'unknown',
        sub_category_id: prediction.subcategory_id || null,
        sub_category_name: prediction.subcategory_name || '',
        category_id: prediction.category_id || null,
        category_name: prediction.category_name || '',
      };
      this.hierarchyCreate.channelForm.show = true;
    },

    onChannelCreated(savedPayload) {
      const response = savedPayload.current ? savedPayload.current.data : savedPayload;
      const entity = response.item || response;
      const channelId = entity.id;
      this.hierarchyCreate.channelForm.show = false;
      // Apply the newly created channel's real ID to the tree
      this.model.tree_id = channelId;
      this.aiGeneratedTreeId = channelId;
      this.aiSuggestionFull = {
        tree_id: channelId,
        confidence: this.hierarchyCreate.prediction.overall_confidence || 0.85,
        rationale: `Created hierarchy: Parent (${this.hierarchyCreate.createdParentId}) → Brand (${this.hierarchyCreate.createdBrandId}) → Channel (${channelId})`,
        parent_id: this.hierarchyCreate.createdParentId,
        brand_id: this.hierarchyCreate.createdBrandId,
        channel_id: channelId,
        hierarchy_level: 'Channel',
        ad_supported: this.hierarchyCreate.prediction.ad_supported || 'unknown',
      };
      this.$flash('success', 'Hierarchy created successfully. Tree ID applied.');
      // Reset
      this.hierarchyCreate.prediction = null;
      this.hierarchyCreate.createdParentId = null;
      this.hierarchyCreate.createdBrandId = null;
    },

    async callMavAPI(domain, countries, expectedTreeId = null) {
      try {
        if (this.isShadowMode() && expectedTreeId) {
          const response = await MAV.post('/shadow/tree/domain', {
            domain: domain,
            expected_tree_id: expectedTreeId,
          });
          return response.data.ai_suggestion || response.data;
        }

        const response = await MAV.post('/predict/domain', {
          domain: domain,
          countries: countries || 'us',
        });
        // Extract prediction data and map to expected format
        const prediction = response.data.prediction || response.data;

        // Implement hierarchy fallback: channel -> brand -> parent
        let tree_id = null;
        let confidence = prediction.overall_confidence;
        let hierarchyLevel = '';
        let hierarchyName = '';

        if (prediction.channel_id && prediction.channel_id !== 0) {
          tree_id = prediction.channel_id;
          confidence = prediction.channel_confidence;
          hierarchyLevel = 'Channel';
          hierarchyName = prediction.channel_name;
        } else if (prediction.brand_id && prediction.brand_id !== 0) {
          tree_id = prediction.brand_id;
          confidence = prediction.brand_confidence;
          hierarchyLevel = 'Brand';
          hierarchyName = prediction.brand_name;
        } else if (prediction.parent_id && prediction.parent_id !== 0) {
          tree_id = prediction.parent_id;
          confidence = prediction.parent_confidence;
          hierarchyLevel = 'Parent';
          hierarchyName = prediction.parent_name;
        }

        return {
          tree_id: tree_id,
          confidence: confidence,
          rationale: `Predicted ${hierarchyLevel}: ${hierarchyName} (Parent: ${
            prediction.parent_name
          }, Brand: ${prediction.brand_name || 'N/A'}, Channel: ${prediction.channel_name ||
            'N/A'})`,
          channel_id: prediction.channel_id,
          channel_name: prediction.channel_name,
          brand_id: prediction.brand_id,
          brand_name: prediction.brand_name,
          parent_id: prediction.parent_id,
          parent_name: prediction.parent_name,
          hierarchy_level: hierarchyLevel,
          is_channel_suggestion: prediction.is_channel_suggestion || false,
          channel_suggestion: prediction.channel_suggestion || null,
          validation_result: prediction.validation_result || null,
          ad_supported: prediction.ad_supported || 'unknown',
          search_query: prediction.search_query || domain,
        };
      } catch (error) {
        console.error('MAV API error:', error);
        throw error;
      }
    },

    async sendShadowEvaluation(domain, expectedTreeId) {
      try {
        await MAV.post('/shadow/tree/domain', {
          domain: domain,
          expected_tree_id: expectedTreeId,
        });
      } catch (error) {
        console.error('Shadow API error:', error);
      }
    },

    canView() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2;
    },

    isV2() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.enabled?.v2;
    },

    canViewTreePrediction() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.enabled?.debug || false;
    },

    isShadowMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (
        mavConfig?.enabled?.shadow === true && !mavConfig?.enabled?.v1 && !mavConfig?.enabled?.v2
      );
    },

    isProductionMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2) && !mavConfig?.enabled?.shadow;
    },

    save() {
      this.createPayloadArray();

      if (this.valid) {
        this.treeSearchRules = this.rules.treeId;
      }

      if (this.aiSuggestionFull && this.model.tree_id) {
        if (this.isProductionMode() && !this.feedbackSent) {
          const action =
            this.aiSuggestionFull.tree_id === this.model.tree_id ? 'ACCEPTED' : 'CORRECTED';
          this.sendFeedback(action, this.model.tree_id);
        }
      }
      this.feedbackSent = false;

      if (this.isShadowMode() && this.model.tree_id) {
        try {
          this.sendShadowEvaluation(this.item.domain, this.model.tree_id);
        } catch (error) {
          console.error('shadow error:', error);
        }
      }

      const url = this.valid
        ? `/domain/discoveries/${this.item.id}/multiplerules`
        : `/domain/discoveries/${this.item.id}/multiplerules/invalid`;
      this.submitClassify(url, {
        failure: () => {
          this.valid = false;
        },
      });
    },
    addLocalization() {
      this.localizations.push({
        tree_id: null,
        country: '',
      });
    },

    removeLocalization(index) {
      this.localizations.splice(index, 1);
    },
    createPayloadArray() {
      const baseObject = {
        pattern: this.model.pattern,
        effective_start_date: this.model.effective_start_date,
      };

      this.payloadArray = [
        {
          ...baseObject,
          tree_id: this.model.tree_id,
          localization: '*',
        },
      ];

      this.localizations.forEach(localization => {
        this.payloadArray.push({
          ...baseObject,
          tree_id: localization.tree_id,
          localization: localization.country,
        });
      });
      this.formattedPayload = { rules: this.payloadArray };
      return this.payloadArray;
    },
    getConfigMap,
  },
};
</script>

<style scoped>
.ai-tree-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;
}

.ai-tree-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
  box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
  transform: translateY(-1px);
}

.ai-tree-button:disabled {
  opacity: 0.5;
}
</style>
